import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

export type UpdatePasswordFormData = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

export type Props = {
  /** Form instance from useForm */
  form: UseFormReturn<UpdatePasswordFormData>;
  /** Handler for form submission */
  onSubmit: () => void;
  /** Handler for cancel action */
  onCancel?: () => void;
  /** Whether the password was updated successfully */
  isSuccess?: boolean;
  /** Render the form fields */
  renderForm: (props: {
    form: UseFormReturn<UpdatePasswordFormData>;
  }) => ReactNode;
  /** Render the action buttons */
  renderActions: (props: {
    isSubmitting: boolean;
    isValid: boolean;
    isDirty: boolean;
    onSubmit: () => void;
    onCancel?: () => void;
  }) => ReactNode;
  /** Render success message */
  renderSuccess?: () => ReactNode;
  /** Container className */
  className?: string;
  /** Gap between sections (default: 16px) */
  gap?: string;
};

/**
 * Base update password form component - defines structure with render props.
 * Each app implements the render props with their own styling.
 */
export function UpdatePasswordFormBase({
  form,
  onSubmit,
  onCancel,
  isSuccess,
  renderForm,
  renderActions,
  renderSuccess,
  className,
  gap = "16px",
}: Props) {
  const { isSubmitting, isValid, isDirty } = form.formState;

  if (isSuccess && renderSuccess) {
    return (
      <div className={className} data-testid="update-password-success">
        {renderSuccess()}
      </div>
    );
  }

  return (
    <div
      className={className}
      data-testid="update-password-form"
      style={{ display: "flex", flexDirection: "column", gap }}
    >
      {renderForm({ form })}
      {renderActions({ isSubmitting, isValid, isDirty, onSubmit, onCancel })}
    </div>
  );
}

export default UpdatePasswordFormBase;
